import { useCallback, useEffect, useState } from 'react'

import { api } from '../api'
import { formatWhen, t } from '../i18n'
import { haptic, notify, openTelegram, useBackButton } from '../telegram'
import type { CoachRequestItem } from '../types'

interface Props {
  onClose: () => void
}

/** Заявки на тренировки — для тренера. Те же ответы, что кнопками в боте. */
export default function CoachInbox({ onClose }: Props) {
  const [items, setItems] = useState<CoachRequestItem[] | null>(null)
  const [busy, setBusy] = useState<number | null>(null)

  useEffect(() => useBackButton(onClose), [onClose])

  useEffect(() => {
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [])

  const load = useCallback(async () => {
    try {
      setItems(await api.coachInbox())
    } catch (e) {
      setItems([])
      notify(e instanceof Error ? e.message : 'Ошибка')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function answer(item: CoachRequestItem, value: 'accept' | 'decline') {
    setBusy(item.id)
    haptic(value === 'accept' ? 'medium' : 'light')
    try {
      const updated = await api.answerCoachRequest(item.id, value)
      setItems((list) => (list ?? []).map((x) => (x.id === updated.id ? updated : x)))
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Ошибка')
      void load()
    } finally {
      setBusy(null)
    }
  }

  const fresh = (items ?? []).filter((i) => i.status === 'new').length

  return (
    <div className="chat-screen" role="dialog" aria-modal="true" aria-label={t().coachInbox(fresh)}>
      <div className="chat-head">
        <button className="ghost chat-back" onClick={onClose} aria-label={t().back}>
          ←
        </button>
        <div className="title chat-title">🎓 {t().coachInbox(fresh)}</div>
      </div>
      <div className="chat-list">
        {items === null && <p className="muted">{t().loading}</p>}
        {items?.length === 0 && <div className="card muted">{t().coachInboxEmpty}</div>}
        {items?.map((item) => (
          <div key={item.id} className="card" style={{ opacity: item.status === 'declined' ? 0.6 : 1 }}>
            <div className="row between" style={{ gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <b>{item.player_name}</b>
                <span className="muted"> · {item.player_level != null ? item.player_level.toFixed(2) : '—'}</span>
              </div>
              <span className={`badge${item.status === 'new' ? ' gold' : item.status === 'declined' ? ' neutral' : ''}`}>{t().coachReqStatus[item.status]}</span>
            </div>
            <div className="muted small" style={{ marginTop: 2 }}>
              {t().fmtName[item.format]} · {formatWhen(item.created_at)}
            </div>
            {item.when_text && <div style={{ marginTop: 6 }}>🕑 {item.when_text}</div>}
            {item.message && <div style={{ marginTop: 6, whiteSpace: 'pre-line' }}>💬 {item.message}</div>}
            {item.status === 'new' && (
              <div className="row" style={{ gap: 8, marginTop: 10 }}>
                <button style={{ flex: 1 }} disabled={busy === item.id} onClick={() => answer(item, 'accept')}>
                  {t().coachAccept}
                </button>
                <button className="ghost" style={{ flex: 1 }} disabled={busy === item.id} onClick={() => answer(item, 'decline')}>
                  {t().coachDecline}
                </button>
              </div>
            )}
            {item.status === 'accepted' &&
              (item.player_link ? (
                <button className="ghost" style={{ width: '100%', marginTop: 10 }} onClick={() => openTelegram(item.player_link!)}>
                  {t().coachContact}
                </button>
              ) : (
                <div className="muted small" style={{ marginTop: 8 }}>
                  {t().coachContactViaBot}
                </div>
              ))}
          </div>
        ))}
      </div>
    </div>
  )
}
